import AddNote from "@assets/note-add.svg";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import Delete from "@mui/icons-material/DeleteTwoTone";
import EditNote from "@mui/icons-material/EditNoteTwoTone";
import TagIcon from "@mui/icons-material/Sell";
import { Chip, IconButton } from "@mui/material";
import type { Annotation } from "@rowan/prisma";
import { useContext, useEffect, useState } from "react";
import { trpc } from "../../utils/trpc";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { getAnnotationId, useDebounce } from "../utils";
import { PrimaryButton } from "./buttons";
import { FlexColContainer, FlexRowContainer } from "./layout";
import { Modal } from "./Modal";
import { TagManagementModal } from "./tags/TagManagementModal";

export const StyledInlineAnnotationMenu = styled(FlexRowContainer)<{
  focused?: boolean;
  smallScreen?: boolean;
}>`
  position: absolute;
  ${({ theme }) => theme.layers.relief.accent_5.shadow};
  width: 330px;
  @supports (width: stretch) {
    width: stretch;
  }
  ${({ smallScreen }) => smallScreen && "width: 300px;"}
  ${({ smallScreen }) => smallScreen && `margin-left: 50px;`}
  border-radius: 8px;
  padding: 10px;
  background-color: white;
  user-select: none;
  color: ${({ theme }) => theme.colours.uIGrey.dark._10};
  z-index: ${({ focused }) => (focused ? 10 : 5)};
  display: flex;
  flex-direction: column;
  border: 1px solid
    ${({ focused, theme }) =>
      focused
        ? theme.colours.uIGrey.light._40
        : theme.colours.uIGrey.light._10};
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;
  transform: ${({ focused, smallScreen }) =>
    focused && !smallScreen
      ? "scale(1) translateY(1)"
      : "scale(0.95) translateY(2px)"};
  ${({ focused, theme }) =>
    focused
      ? theme.layers.relief.accent_10.shadowHigher
      : theme.layers.relief.accent_5.shadow};
  :hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 11;
  }
`;

interface InlineAnnotationMenuProps {
  annotation: Annotation & {
    tags?: {
      id: number;
      annotationId: number;
      tagId: number;
      tag: {
        id: number;
        name: string;
      };
    }[];
  };
  clearHighlight: (id: string) => void;
  deserializeRange: (annotation: Annotation["range"]) => Range | null;
  applyBorder: (range: Range, id: string) => void;
  clearBorder: (id?: string) => void;
}

export const InlineAnnotationMenu = ({
  annotation,
  clearHighlight,
  deserializeRange,
  applyBorder,
  clearBorder,
}: InlineAnnotationMenuProps) => {
  const { focusedId, setFocusedId } = useContext(AnnotationContext);

  const theme = useTheme();
  const annotationId = getAnnotationId(annotation);
  const [noteText, setNoteText] = useState(annotation.noteText || "");
  const [editingText, setEditingText] = useState(
    !!annotation.noteText || false,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const annotationTagIds = annotation.tags?.map((tag) => tag.tagId) || [];

  const updateAnnotation = trpc.updateAnnotation.useMutation();

  const { data: tags = [] } = trpc.getTags.useQuery();
  const sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name));

  const sortedAnnotationTagIds = [...annotationTagIds].sort((a, b) => {
    const nameA =
      tags.find((tag) => tag.id === a)?.name ||
      annotation.tags?.find((tag) => tag.tagId === a)?.tag.name ||
      "";
    const nameB =
      tags.find((tag) => tag.id === b)?.name ||
      annotation.tags?.find((tag) => tag.tagId === b)?.tag.name ||
      "";
    return nameA.localeCompare(nameB);
  });

  const deleteAnnotation = trpc.deleteAnnotations.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      clearHighlight(annotationId);
      clearBorder(annotationId);
      setFocusedId(undefined);
    },
  });

  useEffect(() => {
    const range = deserializeRange(annotation.range);
    if (focusedId === annotationId && range) {
      applyBorder(range, annotationId);
    } else {
      clearBorder(annotationId);
    }
  }, [
    focusedId,
    annotation,
    applyBorder,
    clearBorder,
    deserializeRange,
    annotationId,
  ]);

  const handleUpdate = useDebounce((text: string) => {
    if (text !== annotation.noteText) {
      updateAnnotation.mutate({
        annotationId: annotation.id,
        noteText: text,
      });
    }
  }, 500);

  return (
    <>
      {editingText && (
        <textarea
          id={`annotation-note-${annotation.id}`}
          value={noteText}
          onChange={(e) => {
            const newText = e.target.value;
            setNoteText(newText);
            handleUpdate(newText);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.currentTarget.blur();
            }
          }}
          onBlur={() =>
            updateAnnotation.mutate({
              annotationId: annotation.id,
              noteText,
            })
          }
          style={{
            width: "100%",
            padding: theme.spacing(1),
            borderRadius: "4px",
            backgroundColor: theme.colours.uIGrey.light._10,
            border: "none",
            resize: "none",
            ...theme.customTypography.paragraph._2,
            outline: "none",
            scrollbarWidth: "thin",
          }}
        />
      )}
      <FlexRowContainer
        css={css`
          flex-wrap: wrap;
          justify-content: flex-start;
          width: 100%;
        `}
      >
        {sortedAnnotationTagIds.map((tagId) => {
          const tagName =
            sortedTags.find((tag) => tag.id === tagId)?.name ||
            annotation.tags?.find((tag) => tag.tagId === tagId)?.tag.name;

          if (!tagName) return null;

          return (
            <Chip
              key={tagId}
              label={tagName}
              size="small"
              style={{
                overflow: "hidden",
                maxWidth: "180px",
              }}
            />
          );
        })}
      </FlexRowContainer>
      <FlexRowContainer
        style={{
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          userSelect: "none",
        }}
      >
        <p style={{ ...theme.customTypography.label._4, padding: "0px" }}>
          {`${annotation.createdAt.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })} ● ${annotation.createdAt.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          })}`}
        </p>
        <FlexRowContainer
          style={{
            alignItems: "flex-end",
          }}
        >
          <IconButton
            onClick={() => setEditingText(!editingText)}
            title="Edit annotation note"
            size="small"
            sx={{
              width: "24px",
              height: "24px",
              padding: "0",
              borderRadius: "6px",
              marginBottom: "2px",
            }}
          >
            {annotation.noteText ? (
              <EditNote sx={{ height: "16px", width: "16px" }} />
            ) : (
              <img
                src={AddNote}
                style={{ height: "16px", width: "16px" }}
                alt={"Add annotation note"}
              />
            )}
          </IconButton>
          <TagManagementModal
            tags={sortedTags}
            icon={<TagIcon />}
            annotationId={annotation.id}
            activeTagIds={annotationTagIds}
          />
          <Modal
            key="deleteAnnotationModal"
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
            }}
            renderButton={() => (
              <IconButton
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={deleteAnnotation.isLoading}
                title="Delete annotation"
                size="small"
                sx={{
                  width: "24px",
                  height: "24px",
                  padding: "0",
                  borderRadius: "6px",
                }}
              >
                <Delete sx={{ height: "16px", width: "16px" }} />
              </IconButton>
            )}
            header={<h2>Delete Annotation</h2>}
            content={
              <FlexColContainer
                css={css`
                  gap: 20px;
                `}
              >
                {`Are you sure you want to delete this annotation?`}
              </FlexColContainer>
            }
            footer={
              <PrimaryButton
                onClick={() =>
                  deleteAnnotation.mutate({
                    annotationIds: [annotation.id],
                  })
                }
              >
                Delete
              </PrimaryButton>
            }
          />
        </FlexRowContainer>
      </FlexRowContainer>
    </>
  );
};
